import { getAddressAutosuggest } from '../../api/address';
import { throwGenericError } from '../../error';
import { objectMapping } from '../../api/common';
import { setTrygTealium } from '../../tracking';

export const ADDRESS = 'address';

/**
 * Actions to be connected from redux userData.
 * @type {Object}
 */
export const actionsToConnect = {};

/**
 * Values to be connected from redux userData.
 * @type {Array}
 */
export const propsToConnect = state => state;

/**
 * Sets a suggestion in the form.
 * @param  {Object}   suggestion Object representing a suggestion.
 * @param  {Function} setValues  Set value function of the Formik component.
 * @param  {Array}    values     The current Formik values.
 */
const setFormValues = (suggestion, setValues, values) => {
  const payload = {
    ...values,
    postalCode: (suggestion?.adresse.postnr) ? suggestion?.adresse.postnr : "",
    postalCodeInput: (suggestion?.adresse.postnr) ? suggestion?.adresse.postnr : "",
    city: (suggestion?.adresse.postnrnavn) ? suggestion?.adresse.postnrnavn : "",
    street: (suggestion?.adresse.vejnavn) ? suggestion?.adresse.vejnavn : "",
    houseNumber: (suggestion?.adresse.husnr) ? suggestion?.adresse.husnr : "",
    floorNumber: (suggestion?.adresse.etage) ? suggestion?.adresse.etage : "",
    unitNumber: (suggestion?.adresse.dør) ? suggestion?.adresse.dør : "",
    addressId: (suggestion?.adresse.id) ? suggestion?.adresse.id : "",
    addressInput: (suggestion?.tekst) ? suggestion?.tekst : "",
  };
  setValues(payload);
  const trackingData = {
    customer_address_floor: payload.floorNumber,
    customer_address_house_number: payload.houseNumber,
    customer_address_streetname: payload.street,
    customer_address_door: payload.unitNumber,
    customer_address_zipcode: payload.postalCode,
  };
  setTrygTealium(trackingData);
  objectMapping(values, payload);
};

/**
 * Clears the form.
 * @param  {Function} setValues  Set value function of the Formik component.
 * @param  {Array}    values     The current Formik values.
 */
const clearFormValues = (setValues, values) => {
  const payload = {
    ...values,
    street: null,
    houseNumber: null,
    floorNumber: null,
    unitNumber: null,
    addressInput: null,
  };
  setValues(payload);
};

/**
 * Sets the value of the field.
 * @param  {Object} suggestion Object representing a suggestion.
 * @param  {Object} that       This object from caller.
 */
export const setValue = (suggestion, that) => {
  if (!that.state.isTyping) {
    that.setState({
      value: suggestion.tekst,
    });
  }
};

/**
 * Function that fetches the value to render.
 * @param  {Object} values The current Formik values.
 * @return {string}        The value to show.
 */
export const getValue = (values) => {
  return values.addressInput ? values.addressInput : '';
};

/**
 * How a suggestion looks when selected.
 * @param  {Object}   suggestion Object representing a suggestion.
 * @param  {Function} setValues  Set value function of the Formik component.
 * @param  {Array}    values     The current Formik values.
 * @return {string}              String representation of the suggestion.
 */
export const getSuggestionValue = (suggestion, setValues, values) => {
  setFormValues(suggestion, setValues, values);
  return suggestion.tekst;
};

/**
 * How should the suggestions be rendered in the list.
 * @param  {Object} suggestion Object representing a suggestion.
 * @return {string}            String representation of the suggestion.
 */
export const renderSuggestion = suggestion => (
  suggestion.tekst
);

/**
 * Event to be called on change.
 * @param  {InputEvent} event     The event.
 * @param  {string}     newValue  The current value of the input field.
 * @param  {Object}     that      The "this" object of the caller.
 */
export const onChangeEvent = (event, newValue, that) => {
  that.setState({
    isTyping: true,
    value: newValue,
  });
};

/**
 * Event to call onBlur.
 * @param  {Object}   that      The "this" object of the caller.
 * @param  {Object}   suggestion The suggestion.
 * @param  {Function} setValues  The set values function of the Formik component.
 * @param  {Object}   values     The current values of the Formik componenet.
 */
export const onBlurEvent = (that, suggestion, setValues, values) => {
  if (!suggestion) {
    return;
  }
  setFormValues(suggestion, setValues, values);
  setValue(suggestion, that);
};

/**
 * onSuggestionsFetchRequested is called whenever new suggestions should be fetched.
 * @param  {string}   value      The value entered into the input field.
 * @param  {Object}   that       The "this" object of the caller.
 * @param  {function} dispatch   The dispatch function of the store.
 * @param  {Function} setValues  Set values function of the Formik component.
 * @param  {Array}    values     The current Formik values.
 */
export const onSuggestionsFetchRequested = (value, that, dispatch, setValues, values) => {
  if (value.length === 0) {
    clearFormValues(setValues, values);
    that.setState({
      suggestions: [],
    });
  }

  that.setState({
    isLoading: true,
  });

  const params = {
    'q': value,
  };

  // Add postalCode if needed.
  if (values.postalCode) {
    params.postalCode = values.postalCode;
  }

  getAddressAutosuggest(params, dispatch)
    .then(data => {
      that.setState({
        suggestions: data,
        isLoading: false,
      });
      if (data.length === 1) {
        const suggestion = data[0];
        setFormValues(suggestion, setValues, values);
        setValue(suggestion, that);
      } else if (data.length === 0) {
        clearFormValues(setValues, values);
      }
    })
    .catch(() => {
      const errorData = {
        errorMsg: 'Something went wrong when contacting the address autosuggest service of Dawa.',
      };
      throwGenericError(errorData, dispatch);
    });
};

/**
 * onSuggestionsClearRequested is called whenever the suggestions should be cleared.
 * @param  {Object} that  The "this" object of the caller.
 * @param  {Function} setValues  Set values function of the Formik component.
 * @param  {Array}    values     The current Formik values.
 */
export const onSuggestionsClearRequested = (that, setValues, values) => {
  if (that.state.value.length === 0) {
    clearFormValues(setValues, values);
  }
  that.setState({
    suggestions: [],
  });
};

export const type = ADDRESS;
